import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import {
  Box,
  Divider,
  Grid,
  List,
  ListItem,
  Typography,
  styled,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { getExpandState } from "../../common/services/Utilities";
import ResultRunDate from "../../components/ResultRunDate";
import SelectFilterField from "../../components/SelectFilterField";
import TextFilterField from "../../components/TextFilterField";
import AccordionDropdown from "../../containers/AccordionDropdown";
import MultiDropdownInput from "../../containers/MultiDropdownInput";
import SearchForm from "../../containers/SearchForm";
import useAppStateToFormChanges from "../../hooks/useAppStateToFormChanges";
import useMinLengthErrorValidation from "../../hooks/useMinLengthErrorValidation";
import { Form, Query } from "../../interfaces";
import { activeStatus, selectedStatesAlpha } from "../../refdata/FilterOptions";
import {
  bankFindFormSelectChoices,
  defaultSearchFieldInputLookup,
  numericalRegEx,
} from "../../refdata/constants";
import IncludeCheckbox from "../IncludeCheckbox";
import MoreInfoButton from "../MoreInfoButton";
import { fetchData } from "../../common/services/ApiClient";
import { ApiBaseUrl, AppUrl } from "../../config";
import { initialAppState } from "../../common/reducers/appState";
import { useInitialPageLoad } from "../../hooks/useInitialPageLoad";

const StyledIcon = styled(FiberManualRecordIcon)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: 10,
  marginRight: 10,
  marginTop: 8,
}));

export interface BankFindForm extends Form {
  cert: string;
  zip: string;
  stalp: string;
  name: string;
  url: string;
  branchOffices: boolean;
  city: string;
  activeStatus: number[];
  county: string;
}

export default function BankFindSearchForm({
  appState,
  resetState,
}: {
  appState: Query;
  createTimestamp: string;
  resetState: (appState: Query) => void;
}) {
  const history = useHistory();

  const [reset, setReset] = useState(false);

  useEffect(() => {
    if (reset) {
      setReset(false);
      resetState(initialAppState.bankFind);
      history.push(`${AppUrl("BankFind")}`);
    }
  }, [appState, reset, resetState, history]);

  useInitialPageLoad("bankFind");

  const [bankFindForm, setBankFindForm] = useState<BankFindForm>({
    cert: appState.filters.cert,
    zip: appState.filters.zip,
    stalp: appState.filters.stalp,
    activeStatus: appState.filters.activeStatus,
    name: appState.search.name,
    url: appState.search.url,
    branchOffices: appState.ui.branchOffices,
    city: appState.search.city,
    county: appState.search.countyInst,
  });
  const activeStatusOptions = activeStatus.map(status => {
    return { label: status.label, value: status.value.toString() };
  });

  const bankFindErrorMetaData = {
    city: {
      minLength: 3,
      helperText: "Minimum Length of 3 Characters",
    },
    name: {
      minLength: 4,
      helperText: "Minimum Length of 4 Characters",
    },
  };

  const [errors, setErrors] = useState<Set<string>>(new Set());
  const [disabled, setDisabled] = useState<boolean>(false);
  const [initState, setInitState] = useState(true);
  const location = useLocation();
  const [counties, setCounties] = useState<{ label: string; value: string }[]>(
    [],
  );

  useEffect(() => {
    setDisabled(errors.size !== 0);
  }, [errors]);

  useMinLengthErrorValidation(bankFindForm, bankFindErrorMetaData, setErrors);

  const { prevForm, setPrevForm } = useAppStateToFormChanges<BankFindForm>(
    appState.filters,
    appState.search,
    setBankFindForm,
  );

  //needs to reset to undefined so routing doesnt save state when no params exist
  useEffect(() => {
    if (
      initState &&
      bankFindForm.activeStatus &&
      location &&
      !location.search.includes("activeStatus")
    ) {
      setBankFindForm({ ...bankFindForm, activeStatus: undefined as any });
      setInitState(false);
    }
  }, [initState, bankFindForm, location]);

  useEffect(() => {
    if (!appState.filters.activeState && !bankFindForm.activeStatus) {
      setBankFindForm({ ...bankFindForm, activeStatus: [0, 1] });
    }
  }, [appState.filters.activeState, bankFindForm]);

  const getInitialSearchField = () => {
    return appState.filters.cert
      ? "cert"
      : appState.search.url
      ? "url"
      : "name";
  };
  const filter = bankFindForm.stalp ? `STALP:${bankFindForm.stalp}` : null;

  useEffect(() => {
    if (filter) {
      let isMounted = true;
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/locations?filters=${filter}&agg_by=COUNTY&agg_limit=10000`,
        )
          .then(response => {
            if (isMounted) {
              const formattedCounties = response.data.map((e: any) => ({
                label: e.COUNTY,
                value: e.COUNTY,
              }));
              setCounties(formattedCounties);
            }
          })
          .catch((error: Error) => {
            console.error(error);
          });
      })();
      return () => {
        isMounted = false;
      };
    }
  }, [filter, bankFindForm.stalp]);
  const BankFindFormList = (
    <List>
      <ListItem>
        <ResultRunDate />
      </ListItem>
      <ListItem>
        <Typography style={{ padding: 0, lineHeight: "1rem" }} variant="h4">
          Name/Cert/Website
        </Typography>
      </ListItem>
      <ListItem style={{ display: "block" }}>
        <MultiDropdownInput
          selectId="name-cert-url"
          form={bankFindForm}
          setForm={setBankFindForm}
          initialSearchFieldName={getInitialSearchField()}
          formSelectChoices={bankFindFormSelectChoices}
          searchFieldInputLookup={defaultSearchFieldInputLookup}
          errors={errors}
          formMetaData={bankFindErrorMetaData}
        />
      </ListItem>
      <Divider style={{ margin: 0 }} variant="middle" component="li" />
      <ListItem>
        <AccordionDropdown
          id="activeStatus"
          title="Status"
          defaultExpand={getExpandState(appState.ui.activeStatusExpand, true)}
          body={
            <Grid container>
              <Grid item sm={10}>
                <SelectFilterField
                  currentValue={
                    bankFindForm.activeStatus
                      ? bankFindForm.activeStatus.join()
                      : ""
                  }
                  fieldName="activeStatus"
                  options={activeStatusOptions}
                  form={bankFindForm}
                  setForm={setBankFindForm}
                  arrayOfVals
                />
              </Grid>
              <Grid item sm={2}>
                <MoreInfoButton
                  id="active-inactive-info"
                  popperPosition="auto"
                  label="Banks"
                  hover
                  wide={532}
                >
                  <Typography
                    sx={{
                      marginBottom: 0,
                    }}
                    textAlign="center"
                    variant="h3"
                  >
                    Active vs Inactive Institutions
                  </Typography>
                  <Typography variant="body2" style={{ padding: 0 }}>
                    The FDIC assigns the status of “active” or “inactive” to
                    institutions based on the following criteria:
                  </Typography>
                  <List>
                    <ListItem>
                      <Typography variant="body2" paddingBottom={0}>
                        <StyledIcon /> <strong>Active</strong> - Institutions
                        that are currently open and insured by the FDIC.
                      </Typography>
                    </ListItem>
                    <ListItem>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          flexWrap: "wrap",
                        }}
                      >
                        <StyledIcon />
                        <Typography
                          sx={{
                            flex: 1,
                          }}
                          variant="body2"
                        >
                          <strong>Inactive</strong> - Institutions that have
                          either closed or are no longer doing business under
                          their previously issued FDIC CERT (due to merger into
                          another institution, closure, or failure); or an
                          institution that no longer carries FDIC insurance.)
                        </Typography>
                      </Box>
                    </ListItem>
                  </List>
                </MoreInfoButton>
              </Grid>
            </Grid>
          }
        />
      </ListItem>
      <ListItem>
        <AccordionDropdown
          id="locations"
          title="Locations"
          defaultExpand={getExpandState(appState.ui.locationsExpand, true)}
          body={
            <List>
              <ListItem>
                <SelectFilterField
                  currentValue={bankFindForm.stalp}
                  fieldName="stalp"
                  label="State/Territory"
                  options={selectedStatesAlpha}
                  form={bankFindForm}
                  setForm={setBankFindForm}
                />
              </ListItem>
              <ListItem>
                <SelectFilterField
                  currentValue={
                    bankFindForm.stalp === "" ? "" : bankFindForm.county
                  }
                  fieldName="county"
                  label="County"
                  options={counties}
                  form={bankFindForm}
                  setForm={setBankFindForm}
                  disabled={bankFindForm.stalp === ""}
                />
              </ListItem>
              <ListItem>
                <TextFilterField
                  autoSuggest
                  currentValue={bankFindForm.city}
                  fieldName="city"
                  label="City"
                  apiPath="institutions"
                  form={bankFindForm}
                  setForm={setBankFindForm}
                  error={errors.has("city")}
                  helperText={
                    errors.has("city")
                      ? bankFindErrorMetaData.city.helperText
                      : ""
                  }
                />
              </ListItem>
              <ListItem>
                <TextFilterField
                  currentValue={bankFindForm.zip}
                  apiPath="institutions"
                  fieldName="zip"
                  label="Zip Code"
                  regEx={numericalRegEx}
                  form={bankFindForm}
                  setForm={setBankFindForm}
                />
              </ListItem>
              <ListItem
                id="includeBranchOffices"
                style={{ margin: "-12px 0 0 18px" }}
              >
                <IncludeCheckbox
                  currentValue={bankFindForm.branchOffices}
                  form={bankFindForm}
                  setForm={setBankFindForm}
                  label="Include Branch Offices"
                  fieldName="branchOffices"
                />
                <MoreInfoButton
                  id="includeBranchOffices"
                  popperPosition="right"
                  label="Include Branch Office Information"
                  hover
                >
                  <Typography>
                    This option will give results that include institutions with
                    branch offices that match the search criteria. Deselecting
                    this option will limit the results to institution main
                    offices.
                  </Typography>
                </MoreInfoButton>
              </ListItem>
            </List>
          }
        />
      </ListItem>
    </List>
  );
  return (
    <SearchForm
      header="Search For Institutions"
      noChangeSubmit={!appState.ui.searchPush}
      prevForm={prevForm}
      setPrevForm={setPrevForm}
      canHideSearchPanel={false}
      formList={BankFindFormList}
      form={bankFindForm}
      disabled={disabled}
      optionalIndicator
      displayReset
      onReset={() => setReset(true)}
    />
  );
}
